import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import serviceData from '../../data/services';
import { motion } from 'framer-motion';
import { Code2, CircuitBoard, Cpu, Workflow } from 'lucide-react';
import SectionHeading from '../../components/SectionHeading';
import { colors } from '../../theme';
import { ease } from '../../utils/animations';

const ICON = 26;

// Reads like the hero -- software, then AI -- and then where it runs: on the
// device, and against the systems a business already has.
const icons = [
    <Code2 size={ICON} color={colors.green} />,
    <Cpu size={ICON} color={colors.green} />,
    <CircuitBoard size={ICON} color={colors.green} />,
    <Workflow size={ICON} color={colors.green} />,
];
const services = serviceData.map((s, i) => ({ ...s, icon: icons[i] }));

/**
 * Four items in a 2x2. The numbered list this replaced was a workaround for
 * five items fitting no grid -- and numbering implied a sequence these four
 * don't have. Same full-width measure as every other section.
 */
const ServicesSection = () => (
    <Box
        id="services"
        component="section"
        sx={{ py: { xs: 9, md: 13 }, scrollMarginTop: { xs: '56px', md: '64px' } }}
    >
        <Container maxWidth="lg">
            <SectionHeading eyebrow="Services" title="What we do." />
            <Typography
                color="text.secondary"
                sx={{ mt: 2.5, mb: { xs: 5, md: 8 }, maxWidth: 620, fontSize: '1.05rem', lineHeight: 1.75 }}
            >
                We build custom software, put AI to work inside it, and run it on
                real devices — connected to the systems you already use. Based in
                Minneapolis; we work with teams anywhere.
            </Typography>

            <Grid container rowSpacing={{ xs: 4, md: 6 }} columnSpacing={{ xs: 0, md: 10 }}>
                {services.map((service, i) => (
                    <Grid size={{ xs: 12, md: 6 }} key={service.title}>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.5, delay: (i % 2) * 0.08, ease }}
                        >
                            <Box sx={{ borderTop: '1px solid #e8edeb', pt: { xs: 3, md: 3.5 } }}>
                                <Box sx={{ display: 'flex', mb: 2 }}>{service.icon}</Box>
                                <Typography
                                    component="h3"
                                    sx={{ fontWeight: 600, fontSize: '1.12rem', lineHeight: 1.4, mb: 1.25 }}
                                >
                                    {service.title}
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    sx={{ fontSize: '1rem', lineHeight: 1.75 }}
                                >
                                    {service.description}
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ mt: { xs: 5, md: 7 }, pl: 2.5, borderLeft: `2px solid ${colors.green}` }}>
                <Typography sx={{ fontSize: '0.98rem', lineHeight: 1.7, color: '#111' }}>
                    <strong>Also available:</strong>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                        AI training and workshops for teams and leaders.
                    </Box>
                </Typography>
            </Box>
        </Container>
    </Box>
);

export default ServicesSection;
